import React, { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useFilters, useFlexLayout, useRowSelect, useSortBy, useTable } from 'react-table';
import { BringgTable, Collapse } from '@bringg/react-components';
import { DeliveryBlockResource, ParkingSpot, ServiceArea, Vehicle } from '@bringg/types';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

import ResourcesActions from '../resources-actions/resources-actions';
import useResources, { COLUMN_ACCESSORS } from '../../hooks/use-resources';
import useSelectedResources from '../../hooks/use-selected-resources';
import useUnavailableResources from '../../hooks/use-unavailable-resources';
import Driver from '../../../../../stores/drivers/domain-object/driver';
import { useStores } from '../../../../../recipes';
import * as trailersProvider from '../../../../../stores/vehicles/trailers-provider';
import { useControlledTableState } from 'bringg-web/hooks/use-controlled-state';
import { useTableFilters } from 'bringg-web/hooks/use-table-filters';

import './resources-panel.scss';

type Filters = {
	searchText: string;
};
interface Props {
	deliveryBlockResources: DeliveryBlockResource[];
	deliveryBlockCapacity: number;
	deliveryBlockTimeRange: { startTime: string; endTime: string };
	teamId: number;
	onResourcesChange: (resources: DeliveryBlockResource[]) => void;
	onCapacityChange: (capacity: number) => void;
	onUnavailableSelectedDriversChange: (unavailableDriversCount: number) => void;
	onUnavailableSelectedVehiclesChange: (unavailableVehiclesCount: number) => void;
	onUnavailableSelectedTrailersChange: (unavailableTrailersCount: number) => void;
	parkingSpots: ParkingSpot[];
	deliveryBlockId?: number;
	props?: any;
	serviceAreas: ServiceArea[];
}

const { Panel } = Collapse;

export const ResourcesPanel = ({
	deliveryBlockResources,
	deliveryBlockCapacity,
	deliveryBlockTimeRange,
	teamId,
	onResourcesChange,
	onCapacityChange,
	parkingSpots,
	onUnavailableSelectedDriversChange,
	onUnavailableSelectedVehiclesChange,
	onUnavailableSelectedTrailersChange,
	deliveryBlockId,
	serviceAreas,
	...props
}: Props) => {
	const { driversStore } = useStores();
	const [capacity, setCapacity] = useState<number>(deliveryBlockCapacity);
	const [searchText, setSearchText] = useState('');

	const drivers = useMemo(() => {
		const driversByTeam = driversStore.allByTeam(teamId) ?? [];
		const driversArray = [];
		driversArray.push(...driversByTeam);
		const driversMap = new Map<number, Driver>();
		driversArray.forEach(driver => {
			driversMap.set(driver.id, driver);
		});
		return driversMap;
	}, [teamId]);

	const vehicles = useMemo(() => {
		const vehiclesByTeam = trailersProvider.allNonTrailersByTeam(teamId) ?? [];
		const vehiclesArray = [];
		vehiclesArray.push(...vehiclesByTeam);

		const vehiclesMap = new Map<number, Vehicle>();
		vehiclesArray.forEach(vehicle => {
			vehiclesMap.set(vehicle.id, vehicle);
		});
		return vehiclesMap;
	}, [teamId]);

	const trailers = useMemo(() => {
		const trailersArray = trailersProvider.allTrailersByTeam(teamId);
		const trailersMap = new Map<number, Vehicle>();
		trailersArray.forEach(trailer => {
			trailersMap.set(trailer.id, trailer);
		});
		return trailersMap;
	}, [teamId]);

	const { unavailableDriverIds, unavailableVehicleIds, unavailableTrailerIds } = useUnavailableResources(
		drivers,
		vehicles,
		trailers,
		deliveryBlockTimeRange,
		teamId,
		deliveryBlockId
	);

	const {
		selectedVehicles,
		selectedTrailers,
		selectedDrivers,
		setSelectedVehicles,
		setSelectedDrivers,
		setSelectedTrailers,
		updateSelected
	} = useSelectedResources(
		teamId,
		unavailableDriverIds,
		unavailableVehicleIds,
		unavailableTrailerIds,
		deliveryBlockTimeRange,
		onUnavailableSelectedDriversChange,
		onUnavailableSelectedVehiclesChange,
		onUnavailableSelectedTrailersChange
	);

	const { getTableData, getTableColumns } = useResources(
		drivers,
		vehicles,
		trailers,
		serviceAreas,
		teamId,
		deliveryBlockResources,
		parkingSpots,
		selectedVehicles,
		selectedDrivers,
		selectedTrailers,
		deliveryBlockTimeRange,
		unavailableDriverIds,
		unavailableVehicleIds,
		unavailableTrailerIds,
		() => emitResourceChange([...deliveryBlockResources]),
		resource => removeResource(resource)
	);

	const { t } = useTranslation();

	useEffect(() => {
		onCapacityChange(capacity);
	}, [capacity]);

	useEffect(() => {
		updateSelected(deliveryBlockResources);
	}, []);

	const getHeader = () => {
		const resourcesCount = (deliveryBlockResources as DeliveryBlockResource[]).length;
		return (
			<div className="panel-title">
				<BringgIcon iconName={BringgFontIcons.List} className="panel-title-icon" />
				<span className="panel-title-translate">{t('DELIVERY_BLOCKS.RESOURCES.TITLE')}</span>
				<span className="resources-summary">
					{resourcesCount === 0
						? t('DELIVERY_BLOCKS.RESOURCES.NO_SET')
						: `${t('DELIVERY_BLOCKS.RESOURCES.TITLE')} ${resourcesCount}/${capacity}`}
				</span>
			</div>
		);
	};

	const onCapacityChanged = (value: number) => {
		if (value < deliveryBlockResources.length) {
			return;
		}

		setCapacity(value);
	};

	const addResource = (resource: DeliveryBlockResource) => {
		emitResourceChange([...deliveryBlockResources, resource]);
	};

	const removeResource = (resource: DeliveryBlockResource) => {
		emitResourceChange(deliveryBlockResources.filter(item => item !== resource));
	};

	const clearResources = () => {
		emitResourceChange([]);
		setSelectedVehicles(new Set());
		setSelectedDrivers(new Set());
		setSelectedTrailers(new Set());
	};

	const emitResourceChange = resources => {
		updateSelected(resources);
		onResourcesChange(resources);
	};

	const isAllowedToAddResource = () => {
		return deliveryBlockResources.length < capacity;
	};

	const addDriverToSelectedDrivers = (driverId: number) => {
		setSelectedDrivers(prevState => new Set(prevState.add(driverId)));
	};

	const removeDriverFromSelectedDrivers = (driverId: number) => {
		setSelectedDrivers(prevState => {
			return new Set(Array.from(prevState).filter(id => id !== driverId));
		});
	};

	const addDriver = (driverId: number) => {
		if (!isAllowedToAddResource()) {
			return;
		}
		addDriverToSelectedDrivers(driverId);

		addResource({
			user_id: driverId
		} as DeliveryBlockResource);
	};

	const removeDriver = (driverId: number) => {
		removeDriverFromSelectedDrivers(driverId);

		removeResource(deliveryBlockResources.find(resource => resource.user_id === driverId));
	};

	const addVehicleToSelectedVehicles = (vehicleId: number) => {
		setSelectedVehicles(prevState => new Set(prevState.add(vehicleId)));
	};

	const removeVehicleFromSelectedVehicles = (vehicleId: number) => {
		setSelectedVehicles(prevState => {
			return new Set(Array.from(prevState).filter(id => id !== vehicleId));
		});
	};

	const addVehicle = (vehicleId: number) => {
		if (!isAllowedToAddResource()) {
			return;
		}
		addVehicleToSelectedVehicles(vehicleId);
		const resource: Partial<DeliveryBlockResource> = {
			vehicle_id: vehicleId
		};
		// @ts-ignore
		const trailerAttachToVehicle = vehicles.get(vehicleId)?.trailer?.id;
		if (trailerAttachToVehicle) {
			resource.use_default_trailer = true;
		}

		addResource(resource as DeliveryBlockResource);
	};

	const removeVehicle = (vehicleId: number) => {
		removeVehicleFromSelectedVehicles(vehicleId);
		removeResource(deliveryBlockResources.find(resource => resource.vehicle_id === vehicleId));
	};

	const isResourceChangeDisabled = () => {
		return deliveryBlockResources.length === capacity;
	};

	const tableFiltersState = useTableFilters<Filters>({ defaultFilters: { searchText: '' } });
	const filtersFields = {
		searchText: [
			COLUMN_ACCESSORS.VEHICLE_NAME,
			COLUMN_ACCESSORS.DRIVER_NAME,
			COLUMN_ACCESSORS.TRAILER_NAME,
			COLUMN_ACCESSORS.AVOID_AREA_NAME,
			COLUMN_ACCESSORS.SERVICE_AREA_NAME,
			COLUMN_ACCESSORS.START_LOCATION,
			COLUMN_ACCESSORS.END_LOCATION
		]
	};
	const useControlledState = useControlledTableState<Filters>(tableFiltersState.filters, filtersFields);

	const tableInstance = useTable<(typeof getTableData)[number]>(
		{
			columns: getTableColumns,
			data: getTableData,
			useControlledState,
			filterTypes: {
				byName: (rows, filterIds, value) => {
					return rows.filter(row => {
						const valueLowerCase = value.toLowerCase();
						return (
							row.original.vehicleName.name?.toLowerCase().includes(valueLowerCase) ||
							row.original.driverName.name?.toLowerCase().includes(valueLowerCase) ||
							row.original.startLocation?.name?.toLowerCase().includes(valueLowerCase) ||
							row.original.endLocation?.name?.toLowerCase().includes(valueLowerCase) ||
							row.original.resource.service_areas?.some(service_area =>
								// @ts-ignore
								service_area.name.toLowerCase().includes(valueLowerCase)
							) ||
							(row.original.resource.use_default_trailer
								? row.original.vehicleName.trailer?.name.toLowerCase().includes(valueLowerCase)
								: row.original.trailerName.name?.toLowerCase().includes(valueLowerCase))
						);
					});
				}
			}
		},
		useFilters,
		useSortBy,
		useRowSelect,
		useFlexLayout
	);

	const handleFilterChange = e => {
		const value = e.target.value;
		setSearchText(value);
		tableFiltersState.updateFilters({ searchText: value });
	};

	return (
		<Panel
			{...props}
			key="3"
			header={getHeader()}
			showArrow
			className="delivery-block-panel resources-panel disable-when-select-is-open"
		>
			<ResourcesActions
				capacity={{ value: capacity, onCapacityChanged }}
				resources={{ clearResources, isResourceChangeDisabled, length: deliveryBlockResources.length }}
				drivers={{ drivers, addDriver, removeDriver, selectedDrivers }}
				vehicles={{ vehicles, addVehicle, removeVehicle, selectedVehicles }}
				unavailableTrailerIds={unavailableTrailerIds}
				unavailableVehicleIds={unavailableVehicleIds}
				unavailableDriverIds={unavailableDriverIds}
			/>
			<input
				type="text"
				placeholder={t('NEW_DELIVERY_BLOCK_MODAL_RESOURCES_PANEL.SEARCH_INPUT')}
				value={searchText}
				onChange={handleFilterChange}
			/>
			<BringgTable tableInstance={tableInstance} maxHeight={350} />
			{deliveryBlockResources.length === 0 && (
				<div className="table-empty-state-container">
					<span className="table-empty-state" data-test-id="table-empty-state">
						{t('DELIVERY_BLOCKS.RESOURCES.EMPTY_STATE')}
					</span>
				</div>
			)}
		</Panel>
	);
};
